import React, { useState, useMemo, useEffect } from 'react';
import { DailyRecord, AppState, FoodItem, DashboardDailyMetrics, Exercise } from '../types';
import { 
  DEFAULT_SWEDISH_TASKS, 
  loadAppState, 
  toPersianDigits,
  getRecordsRange
} from '../lib/storage';
import { apiGet } from '../lib/api';
import { useLanguage, formatNumberByLang } from '../lib/i18n';
import { getDashboardMetricsRange, subscribeToPlannerChanges } from '../lib/plannerClient';
import { calculateDailyMetrics } from '../lib/scoringEngine';
import { getLearningTopics, getLearningSessions } from '../lib/learningStorage';
import { getLocalizedFoodText, getLocalizedFoodQuantity } from '../lib/foodTranslations';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  AreaChart, 
  Area, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ReferenceLine 
} from 'recharts';
import { 
  Calendar, 
  FileBarChart, 
  Download, 
  Copy, 
  Check, 
  Activity, 
  Apple, 
  Moon, 
  Droplets, 
  TrendingUp, 
  Award, 
  CheckCircle2,
  ShieldAlert, 
  Sparkles, 
  AlertCircle, 
  Brain, 
  Briefcase, 
  Flame, 
  Heart, 
  Search, 
  Printer, 
  FileText, 
  PieChart as PieIcon,
  BarChart2,
  ListFilter
} from 'lucide-react';

interface ReportsProps {
  currentDate: string;
  record: DailyRecord;
}

type TimeRange = '7d' | '30d' | '90d' | 'all';
type ReportSectionTab = 'charts' | 'nutrition' | 'rehab' | 'sleep_work' | 'ai_export';

export default function ReportsModule({ currentDate, record }: ReportsProps) {
  const { lang, t } = useLanguage();
  const fmt = (v: number | string) => formatNumberByLang(v, lang);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [activeSection, setActiveSection] = useState<ReportSectionTab>('charts');
  const [foodSearch, setFoodSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [plannerMetricsByDate, setPlannerMetricsByDate] = useState<Record<string, DashboardDailyMetrics>>({});
  const [allExercises, setAllExercises] = useState<Exercise[]>([]);

  useEffect(() => {
    apiGet('/api/rehab/exercises')
      .then(res => {
        if (res.success && Array.isArray(res.data)) {
          setAllExercises(res.data);
        }
      })
      .catch(err => console.warn("Failed to load exercises in ReportsModule:", err));
  }, []);

  const rehabExercisesList = useMemo(() => {
    return allExercises.filter(e => e.category === 'rehab' || e.category === 'physiotherapy' || !e.category);
  }, [allExercises]);

  const fatLossExercisesList = useMemo(() => {
    return allExercises.filter(e => e.category === 'fat-loss' || e.category === 'cardio');
  }, [allExercises]);

  // Load app state
  const appState = loadAppState();
  const allRecordsObj = appState.records || {};
  const userSettings = (appState.settings || {}) as AppState['settings'];

  // Compute date list for the selected time range
  const dateRangeList = useMemo(() => {
    const today = new Date(currentDate);
    if (isNaN(today.getTime())) {
      return [currentDate];
    }

    let daysCount = 30;
    if (timeRange === '7d') daysCount = 7;
    if (timeRange === '30d') daysCount = 30;
    if (timeRange === '90d') daysCount = 90;

    if (timeRange === 'all') {
      const keys = Object.keys(allRecordsObj).sort();
      if (keys.length === 0) return [currentDate];
      const firstDateStr = keys[0];
      const start = new Date(firstDateStr);
      const end = new Date();
      const list: string[] = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        list.push(d.toISOString().split('T')[0]);
      }
      return list.length > 0 ? list : [currentDate];
    }

    const list: string[] = [];
    for (let i = daysCount - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      list.push(d.toISOString().split('T')[0]);
    }
    return list;
  }, [currentDate, timeRange, allRecordsObj]);

  // Synchronize Canonical Planner Occurrences & Scoring Engine for the date range
  useEffect(() => {
    if (dateRangeList.length === 0) return;
    const startDate = dateRangeList[0];
    const endDate = dateRangeList[dateRangeList.length - 1];

    let isMounted = true;
    const fetchRange = () => {
      getDashboardMetricsRange(startDate, endDate).then(data => {
        if (isMounted && data) {
          setPlannerMetricsByDate(data);
        }
      }).catch(err => {
        console.warn("[ReportsModule] Failed to fetch planner range:", err);
      });
    };

    fetchRange();
    const unsubscribe = subscribeToPlannerChanges(() => {
      fetchRange();
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [dateRangeList]);

  // Aggregate records for the date range with Canonical Dashboard v2.2 Metrics
  const recordsData = useMemo(() => {
    return dateRangeList.map(dateStr => {
      const rec = allRecordsObj[dateStr] || {
        date: dateStr,
        rehabCompletedIds: [],
        fatLossCompletedIds: [],
        foods: [],
        swedishCompletedIds: [],
        wentToWork: false,
        attendedSfi: false,
        activities: [],
        notes: { painLevel: 0, mood: 'Calm', energyLevel: 3, symptoms: '', observations: '', reminders: '' },
        waterIntake: 0,
        sleepHours: 0,
        weight: undefined
      };

      const rehabCount = rec.rehabCompletedIds?.length || 0;
      const rehabMinutes = rehabExercisesList
        .filter(ex => rec.rehabCompletedIds?.includes(ex.id))
        .reduce((sum, item) => sum + (item.duration || 5), 0);

      const fatLossCount = rec.fatLossCompletedIds?.length || 0;
      const fatLossCalories = fatLossExercisesList
        .filter(ex => rec.fatLossCompletedIds?.includes(ex.id))
        .reduce((sum, item) => sum + (item.caloriesBurned || 15), 0);

      const foodCalories = rec.foods?.reduce((sum, item) => sum + item.calories, 0) || 0;
      const foodProtein = rec.foods?.reduce((sum, item) => sum + item.protein, 0) || 0;
      const foodCarbs = rec.foods?.reduce((sum, item) => sum + item.carbs, 0) || 0;
      const foodFat = rec.foods?.reduce((sum, item) => sum + item.fat, 0) || 0;

      const extraMinutes = rec.activities?.reduce((sum, item) => sum + item.duration, 0) || 0;
      const extraCalories = rec.activities?.reduce((sum, item) => sum + (item.caloriesBurned || 0), 0) || 0;

      const totalBurned = fatLossCalories + extraCalories;
      const activeMinutes = rehabMinutes + (fatLossCount * 3) + extraMinutes;

      // Canonical Single Source of Truth Metrics
      const plannerMetrics = plannerMetricsByDate[dateStr] || calculateDailyMetrics([]);
      const score = plannerMetrics.dailyScore; // number | null (null for Rest Day)
      const availablePoints = plannerMetrics.availablePoints;
      const earnedPoints = plannerMetrics.earnedPoints;
      const bonusPoints = plannerMetrics.bonusPoints;
      const totalTasks = plannerMetrics.totalTasks;
      const completedTasks = plannerMetrics.completedTasks;
      const remainingTasks = plannerMetrics.remainingTasks;
      const completionRate = plannerMetrics.completionRate;
      const dayType = plannerMetrics.dayType || 'standard';
      const scoreStatus = plannerMetrics.scoreStatus || (score !== null ? 'calculated' : 'N/A');

      const painLevel = rec.notes?.painLevel || 0;
      const energyLevel = rec.notes?.energyLevel || 3;
      const sleep = rec.sleepHours || 0;
      const water = rec.waterIntake || 0;

      // Format date label for charts (e.g. "07/23")
      const parts = dateStr.split('-');
      const shortDate = parts.length === 3 ? `${parts[1]}/${parts[2]}` : dateStr;

      return {
        date: dateStr,
        shortDate,
        record: rec,
        score,
        availablePoints,
        earnedPoints,
        bonusPoints,
        totalTasks,
        completedTasks,
        remainingTasks,
        completionRate,
        dayType,
        scoreStatus,
        rehabCount,
        rehabMinutes,
        fatLossCount,
        fatLossCalories,
        foodCalories,
        foodProtein,
        foodCarbs,
        foodFat,
        totalBurned,
        activeMinutes,
        painLevel,
        energyLevel,
        sleep,
        water,
        weight: rec.weight,
        swedishCount: rec.swedishCompletedIds?.length || 0,
        attendedSfi: rec.attendedSfi ? 1 : 0,
        wentToWork: rec.wentToWork ? 1 : 0,
        activitiesCount: rec.activities?.length || 0
      };
    });
  }, [dateRangeList, allRecordsObj, plannerMetricsByDate]);

  // Overall statistics summaries
  const totals = useMemo(() => {
    const daysCount = recordsData.length || 1;
    // Canonical Average Daily Score: only average over days that have calculated scores (excluding Rest Days / N/A)
    const scoredDays = recordsData.filter(d => d.score !== null && typeof d.score === 'number');
    const avgScore = scoredDays.length > 0 
      ? Math.round(scoredDays.reduce((sum, d) => sum + (d.score as number), 0) / scoredDays.length)
      : null;

    const totalTasksScheduled = recordsData.reduce((sum, d) => sum + d.totalTasks, 0);
    const totalTasksCompleted = recordsData.reduce((sum, d) => sum + d.completedTasks, 0);
    const totalAvailablePoints = recordsData.reduce((sum, d) => sum + d.availablePoints, 0);
    const totalEarnedPoints = recordsData.reduce((sum, d) => sum + d.earnedPoints, 0);

    const totalRehabDone = recordsData.reduce((sum, d) => sum + d.rehabCount, 0);
    const totalFatLossDone = recordsData.reduce((sum, d) => sum + d.fatLossCount, 0);
    const totalCaloriesConsumed = recordsData.reduce((sum, d) => sum + d.foodCalories, 0);
    const avgDailyCalories = Math.round(totalCaloriesConsumed / daysCount);
    const totalCaloriesBurned = recordsData.reduce((sum, d) => sum + d.totalBurned, 0);
    const avgDailyProtein = Math.round(recordsData.reduce((sum, d) => sum + d.foodProtein, 0) / daysCount);
    const avgDailyCarbs = Math.round(recordsData.reduce((sum, d) => sum + d.foodCarbs, 0) / daysCount);
    const avgDailyFat = Math.round(recordsData.reduce((sum, d) => sum + d.foodFat, 0) / daysCount);
    const avgWater = Math.round(recordsData.reduce((sum, d) => sum + d.water, 0) / daysCount);
    const avgSleep = Number((recordsData.reduce((sum, d) => sum + d.sleep, 0) / daysCount).toFixed(1));
    const avgPain = Number((recordsData.reduce((sum, d) => sum + d.painLevel, 0) / daysCount).toFixed(1));
    const avgEnergy = Number((recordsData.reduce((sum, d) => sum + d.energyLevel, 0) / daysCount).toFixed(1));
    const totalActiveMinutes = recordsData.reduce((sum, d) => sum + d.activeMinutes, 0);
    const totalWorkDays = recordsData.reduce((sum, d) => sum + d.wentToWork, 0);
    const totalSfiDays = recordsData.reduce((sum, d) => sum + d.attendedSfi, 0);
    const totalSwedishTasks = recordsData.reduce((sum, d) => sum + d.swedishCount, 0);

    // Latest recorded weight
    const weightsWithValues = recordsData.filter(d => d.weight && d.weight > 0);
    const latestWeight = weightsWithValues.length > 0 
      ? weightsWithValues[weightsWithValues.length - 1].weight 
      : userSettings.currentWeight || undefined;

    // All food entries across records
    const allFoodsList: { date: string; food: FoodItem }[] = [];
    recordsData.forEach(d => {
      d.record.foods?.forEach(f => {
        allFoodsList.push({ date: d.date, food: f });
      });
    });

    return {
      daysCount,
      avgScore,
      scoredDaysCount: scoredDays.length,
      totalTasksScheduled,
      totalTasksCompleted,
      totalAvailablePoints,
      totalEarnedPoints,
      totalRehabDone,
      totalFatLossDone,
      totalCaloriesConsumed,
      avgDailyCalories,
      totalCaloriesBurned,
      avgDailyProtein,
      avgDailyCarbs,
      avgDailyFat,
      avgWater,
      avgSleep,
      avgPain,
      avgEnergy,
      totalActiveMinutes,
      totalWorkDays,
      totalSfiDays,
      totalSwedishTasks,
      latestWeight,
      allFoodsList
    };
  }, [recordsData, userSettings]);

  // Learning module stats
  const learningStats = useMemo(() => {
    const topics = getLearningTopics();
    const sessions = getLearningSessions();
    return {
      topicsCount: topics.length,
      sessionsCount: sessions.length
    };
  }, []);

  // Filtered food list
  const filteredFoods = useMemo(() => {
    if (!foodSearch.trim()) return totals.allFoodsList;
    const q = foodSearch.toLowerCase();
    return totals.allFoodsList.filter(item => 
      item.food.name.toLowerCase().includes(q) || 
      item.food.category.toLowerCase().includes(q) ||
      item.date.includes(q)
    );
  }, [totals.allFoodsList, foodSearch]);

  // Donut chart macro data
  const macroPieData = useMemo(() => {
    return [
      { name: t('reportsMacroProtein', 'پروتئین (گرم)'), value: totals.avgDailyProtein, color: '#10B981' },
      { name: t('reportsMacroCarbs', 'کربوهیدرات (گرم)'), value: totals.avgDailyCarbs, color: '#3B82F6' },
      { name: t('reportsMacroFat', 'چربی مفید (گرم)'), value: totals.avgDailyFat, color: '#F59E0B' }
    ];
  }, [totals, t]);

  // Generate markdown output for AI based on selected language
  const aiMarkdownText = useMemo(() => {
    const avgScoreDisplay = totals.avgScore !== null ? `${totals.avgScore}` : 'N/A';

    if (lang === 'sv') {
      return `[AI SYSTEMINSTRUKTIONER / AI SYSTEM INSTRUCTIONS]
Vänligen granska mina fullständiga egencare-, sjukgymnastik-, närings-, sömn- och hälsodata nedan. Baserat på data från de senaste ${totals.daysCount} dagarna fram till ${currentDate}, ge en detaljerad analys av ryggåterhämtning, smärtlindring, hydrering, makronäring och svenska studier.

---

## 📊 Omfattande Hälso- & Framstegsrapport (FULL WELLNESS REPORT)
* **Analysperiod:** Senaste ${totals.daysCount} dagarna (fram till ${currentDate})
* **Genomsnittlig Stabilitetspoäng (Avg Score):** ${avgScoreDisplay} av 100
* **Planerade & Utförda Uppgifter:** ${totals.totalTasksCompleted} av ${totals.totalTasksScheduled} uppgifter slutförda
* **Senaste Registrerade Vikt:** ${totals.latestWeight ? totals.latestWeight + ' kg' : 'Ej registrerad'}

### 🏥 1. Ryggrehabilitering & Träning (Rehab & Spine Therapy)
* Totalt antal ryggrehabövningar: ${totals.totalRehabDone} övningar
* Totalt antal konditionspass: ${totals.totalFatLossDone} pass
* Total fysisk aktivitetstid: ${totals.totalActiveMinutes} minuter (~${(totals.totalActiveMinutes / 60).toFixed(1)} timmar)
* Genomsnittlig ryggsmärta: ${totals.avgPain} av 10
* Genomsnittlig energinivå: ${totals.avgEnergy} av 5

### 🍏 2. Näring & Kaloriintag (Nutrition & Caloric Intake)
* Genomsnittligt dagligt kaloriintag: ${totals.avgDailyCalories} kcal (Mål: ${userSettings.targetCalories || 1767} kcal)
* Förbrända träningskalorier: ${totals.totalCaloriesBurned} kcal
* Genomsnittligt dagligt protein: ${totals.avgDailyProtein} g
* Genomsnittliga dagliga kolhydrater: ${totals.avgDailyCarbs} g
* Genomsnittligt dagligt fett: ${totals.avgDailyFat} g
* Genomsnittligt dagligt vattenintag (Hydration): ${totals.avgWater} ml (Mål: ${userSettings.targetWater || 2000} ml)

### 🛌 3. Sömn & Återhämtning (Sleep & Recovery)
* Genomsnittliga sömntimmar: ${totals.avgSleep} timmar/dag (Mål: 7-8 timmar)

### 💼 4. Arbete & Utbildning (Work & Activity)
* Antal registrerade arbetspass: ${totals.totalWorkDays} pass
* AI-lektioner & studier: ${learningStats.topicsCount} ämnen, ${learningStats.sessionsCount} tillfällen

---
*Kopiera hela texten och klistra in i ChatGPT eller Gemini för fördjupad analys.*`;
    }

    if (lang === 'en') {
      return `[AI SYSTEM INSTRUCTIONS]
Please review my complete self-care, spine rehab, nutrition, sleep, and health tracking data below. Based on the past ${totals.daysCount} days ending on ${currentDate}, provide a detailed analysis of spine recovery, pain management, hydration, macronutrients, and learning progress.

---

## 📊 Comprehensive Health & Progress Report (FULL WELLNESS REPORT)
* **Analysis Period:** Past ${totals.daysCount} days (ending ${currentDate})
* **Average Stability Score:** ${avgScoreDisplay} / 100
* **Scheduled & Completed Tasks:** ${totals.totalTasksCompleted} of ${totals.totalTasksScheduled} tasks completed
* **Latest Recorded Weight:** ${totals.latestWeight ? totals.latestWeight + ' kg' : 'Not recorded'}

### 🏥 1. Spine Rehab & Physical Therapy
* Total spine rehab exercises done: ${totals.totalRehabDone} exercises
* Total cardio workouts done: ${totals.totalFatLossDone} sessions
* Total active physical time: ${totals.totalActiveMinutes} minutes (~${(totals.totalActiveMinutes / 60).toFixed(1)} hours)
* Average spine pain score: ${totals.avgPain} / 10
* Average energy level: ${totals.avgEnergy} / 5

### 🍏 2. Nutrition & Caloric Intake
* Average daily calories: ${totals.avgDailyCalories} kcal (Target: ${userSettings.targetCalories || 1767} kcal)
* Workout calories burned: ${totals.totalCaloriesBurned} kcal
* Average daily protein: ${totals.avgDailyProtein} g
* Average daily carbs: ${totals.avgDailyCarbs} g
* Average daily fat: ${totals.avgDailyFat} g
* Average daily water (Hydration): ${totals.avgWater} ml (Target: ${userSettings.targetWater || 2000} ml)

### 🛌 3. Sleep & Recovery
* Average daily sleep: ${totals.avgSleep} hours/day (Target: 7 to 8 hours)

### 💼 4. Work & Education
* Work shifts recorded: ${totals.totalWorkDays} shifts
* AI learning sessions: ${learningStats.topicsCount} topics, ${learningStats.sessionsCount} sessions

---
*Copy the full prompt above and paste it into ChatGPT, Gemini, or Claude for an in-depth health report.*`;
    }

    return `[دستورالعمل هوش مصنوعی / AI SYSTEM INSTRUCTIONS]
لطفاً داده‌های کامل خودمراقبتی، فیزیوتراپی، تغذیه، میزان خواب و وضعیت سلامتی من در زیر را بررسی کنید. بر اساس داده‌های ${totals.daysCount} روز گذشته منتهی به ${currentDate}، تحلیلی دقیق از روند ریکاوری ستون فقرات کمر، کنترل درد، هیدراتاسیون، تعادل درشتمغذی‌ها و استمرار یادگیری ارائه دهید.

---

## 📊 جامع‌ترین گزارش سلامتی و عملکرد پیشرفت (FULL WELLNESS REPORT)
* **بازه زمانی تحلیل:** ${totals.daysCount} روز اخیر (منتهی به ${currentDate})
* **میانگین امتیاز پایداری (Avg Score):** ${avgScoreDisplay} از ۱۰۰
* **تکمیل اهداف پلنر هفتگی:** ${totals.totalTasksCompleted} از ${totals.totalTasksScheduled} وظیفه برنامه‌ریزی‌شده
* **وزن فعلی ثبت‌شده:** ${totals.latestWeight ? totals.latestWeight + ' کیلوگرم' : 'ثبت‌نشده'}

### 🏥 1. فیزیوتراپی دیسک کمر و تمرینات ورزشی (Rehab & Spine Therapy)
* کل تکرار تمرینات فیزیوتراپی کمر انجام‌شده: ${totals.totalRehabDone} تمرین
* کل تکرار تمرینات کاردیو چربی‌سوزی: ${totals.totalFatLossDone} تمرین
* مجموع زمان فعالیت بدنی: ${totals.totalActiveMinutes} دقیقه (~${(totals.totalActiveMinutes / 60).toFixed(1)} ساعت)
* میانگین نمره درد کمر گزارش‌شده: ${totals.avgPain} از ۱۰
* میانگین سطح انرژی بدنی: ${totals.avgEnergy} از ۵

### 🍏 2. تغذیه و دریافت کالری (Nutrition & Caloric Intake)
* میانگین کالری دریافتی روزانه: ${totals.avgDailyCalories} kcal (سقف هدف: ${userSettings.targetCalories || 1767} kcal)
* کل کالری سوزانده‌شده ورزشی: ${totals.totalCaloriesBurned} kcal
* میانگین پروتئین دریافتی روزانه: ${totals.avgDailyProtein} گرم
* میانگین کربوهیدرات روزانه: ${totals.avgDailyCarbs} گرم
* میانگین چربی مصرفی روزانه: ${totals.avgDailyFat} گرم
* میانگین آب مصرفی روزانه (Hydration): ${totals.avgWater} ml (هدف: ${userSettings.targetWater || 2000} ml)

### 🛌 3. پایش خواب و استراحت (Sleep & Recovery)
* میانگین ساعات خواب روزانه: ${totals.avgSleep} ساعت در روز (هدف: ۷ تا ۸ ساعت)

### 💼 4. فعالیت کاری و آموزشی (Work & Activity)
* تعداد شیفت‌های کاری ثبت‌شده: ${totals.totalWorkDays} شیفت
* مباحث و جلسات درسی هوش مصنوعی: ${learningStats.topicsCount} مبحث، ${learningStats.sessionsCount} جلسه آموزشی

---
*گزارش جامع فوق همراه با الگوریتم‌های پایش لحظه‌ای و پلنر هفتگی کانونیکال ثبت شده است. کل متن را کپی کرده و در ChatGPT یا Gemini جهت تحلیل دقیق قرار دهید.*`;
  }, [lang, totals, userSettings, currentDate, learningStats]);

  const handleCopyMarkdown = () => {
    navigator.clipboard.writeText(aiMarkdownText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportCSV = () => {
    const headers = [
      'Date', 
      'Score', 
      'ScoreStatus',
      'DayType',
      'TasksScheduled',
      'TasksCompleted',
      'CompletionRate(%)',
      'AvailablePoints',
      'EarnedPoints',
      'BonusPoints',
      'PainLevel(0-10)', 
      'EnergyLevel(1-5)', 
      'RehabDoneCount', 
      'FatLossDoneCount', 
      'CaloriesConsumed(kcal)', 
      'Protein(g)', 
      'Carbs(g)', 
      'Fat(g)', 
      'Water(ml)', 
      'Sleep(hrs)', 
      'Weight(kg)', 
      'WentToWork', 
      'AttendedSFI'
    ];
    const rows = recordsData.map(d => [
      d.date,
      d.score !== null ? d.score : 'N/A',
      d.scoreStatus,
      d.dayType,
      d.totalTasks,
      d.completedTasks,
      d.completionRate,
      d.availablePoints,
      d.earnedPoints,
      d.bonusPoints,
      d.painLevel,
      d.energyLevel,
      d.rehabCount,
      d.fatLossCount,
      d.foodCalories,
      d.foodProtein,
      d.foodCarbs,
      d.foodFat,
      d.water,
      d.sleep,
      d.weight || '',
      d.wentToWork ? 'Yes' : 'No',
      d.attendedSfi ? 'Yes' : 'No'
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `vaxa_wellness_report_${currentDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportJSON = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(appState, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `vaxa_full_backup_${currentDate}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <div id="reports-module-view" className="space-y-6 animate-fadeIn pb-12">
      
      {/* Module Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
        <div>
          <h2 className="text-2xl font-bold font-display text-gray-900 dark:text-white flex items-center gap-2.5">
            <div className="p-2 bg-indigo-50 dark:bg-indigo-950/50 rounded-xl text-indigo-600 dark:text-indigo-400">
              <FileBarChart className="w-6 h-6" />
            </div>
            {t('reportsHeaderTitle', 'مرکز گزارش‌های تحلیلی، نمودارها و آمار جامع پیشرفت')}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('reportsHeaderDesc', 'تحلیل کامل تمامی ورودی‌های شما در تمام بخش‌ها (تغذیه، فیزیوتراپی کمر، خواب، فعالیت بدنی، کار و آموزش) همراه با نمودارهای تعاملی پیشرفته.')}
          </p>
        </div>

        {/* Global Controls: Time Range Selector & Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {/* Time range selector pills */}
          <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200/60 dark:border-gray-700/60">
            {(['7d', '30d', '90d', 'all'] as TimeRange[]).map((tr) => (
              <button
                key={tr}
                onClick={() => setTimeRange(tr)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  timeRange === tr
                    ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-xs'
                    : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tr === '7d' ? t('reportsRange7d', '۷ روز اخیر') : tr === '30d' ? t('reportsRange30d', '۳۰ روز اخیر') : tr === '90d' ? t('reportsRange90d', '۹۰ روز') : t('reportsRangeAll', 'کل تاریخچه')}
              </button>
            ))}
          </div>

          <button
            onClick={() => window.print()}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border border-gray-200 dark:border-gray-700"
            title={t('reportsPrintTooltip', 'چاپ یا ذخیره PDF')}
          >
            <Printer className="w-4 h-4 text-indigo-500" />
            <span className="hidden sm:inline">{t('reportsPrintBtn', 'چاپ / PDF')}</span>
          </button>
        </div>
      </div>

      {/* Sub-Section Navigation Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar border-b border-gray-100 dark:border-gray-800">
        <button
          onClick={() => setActiveSection('charts')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
            activeSection === 'charts'
              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700'
          }`}
        >
          <BarChart2 className="w-4 h-4" />
          <span>{t('reportsTabCharts', 'داشبورد نمودارهای تعاملی')}</span>
        </button>

        <button
          onClick={() => setActiveSection('nutrition')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
            activeSection === 'nutrition'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700'
          }`}
        >
          <Apple className="w-4 h-4" />
          <span>{t('reportsTabNutrition', 'گزارش کامل تغذیه و غذاها')}</span>
        </button>

        <button
          onClick={() => setActiveSection('rehab')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
            activeSection === 'rehab'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-500/20'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>{t('reportsTabRehab', 'گزارش فیزیوتراپی و حرکات')}</span>
        </button>

        <button
          onClick={() => setActiveSection('sleep_work')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
            activeSection === 'sleep_work'
              ? 'bg-amber-600 text-white shadow-md shadow-amber-500/20'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700'
          }`}
        >
          <Moon className="w-4 h-4" />
          <span>{t('reportsTabSleepWork', 'خواب و شیفت کاری')}</span>
        </button>

        <button
          onClick={() => setActiveSection('ai_export')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
            activeSection === 'ai_export'
              ? 'bg-violet-600 text-white shadow-md shadow-violet-500/20'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
          }`}
        >
          <Sparkles className={`w-4 h-4 ${activeSection === 'ai_export' ? 'text-amber-300' : 'text-amber-500'}`} />
          <span>{t('reportsTabAiExport', 'خروجی هوش مصنوعی و فایل')}</span>
        </button>
      </div>

      {/* Top Key Performance Indicators Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiTaskCompletion', 'تکمیل وظایف')}</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="text-xl font-extrabold text-gray-900 dark:text-white font-mono">
            {totals.totalTasksScheduled > 0 ? (
              <>
                {fmt(Math.round((totals.totalTasksCompleted / totals.totalTasksScheduled) * 100))}%
              </>
            ) : totals.totalTasksCompleted > 0 ? (
              <>
                {fmt(totals.totalTasksCompleted)} <span className="text-xs text-gray-400 font-normal">{t('reportsKpiTaskUnit', 'مورد')}</span>
              </>
            ) : (
              <span className="text-base text-gray-400 font-bold">---</span>
            )}
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiTaskCompletionSub', '{{done}} از {{total}} وظیفه موظف', { done: fmt(totals.totalTasksCompleted), total: fmt(totals.totalTasksScheduled) })}</span>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiCalories', 'میانگین کالری')}</span>
            <Apple className="w-4 h-4 text-amber-500" />
          </div>
          <div className="text-xl font-extrabold text-gray-900 dark:text-white font-mono">
            {fmt(totals.avgDailyCalories)} <span className="text-xs text-gray-400 font-normal">kcal</span>
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiCaloriesSub', 'هدف: {{target}}', { target: fmt(userSettings.targetCalories || 1767) })}</span>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiRehab', 'فیزیوتراپی کمر')}</span>
            <Activity className="w-4 h-4 text-purple-500" />
          </div>
          <div className="text-xl font-extrabold text-gray-900 dark:text-white font-mono">
            {fmt(totals.totalRehabDone)} <span className="text-xs text-gray-400 font-normal">{t('reportsKpiRehabUnit', 'تمرین')}</span>
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiRehabSub', 'تعداد کل حرکات کامل')}</span>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiPain', 'شاخص درد کمر')}</span>
            <ShieldAlert className="w-4 h-4 text-orange-500" />
          </div>
          <div className="text-xl font-extrabold text-orange-600 dark:text-orange-400 font-mono">
            {fmt(totals.avgPain)} <span className="text-xs text-gray-400 font-normal">{t('reportsPainOf10', 'از ۱۰')}</span>
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiPainSub', 'میانگین شدت درد ستون فقرات')}</span>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiWater', 'آب مصرفی روزانه')}</span>
            <Droplets className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-xl font-extrabold text-blue-600 dark:text-blue-400 font-mono">
            {fmt(totals.avgWater)} <span className="text-xs text-gray-400 font-normal">ml</span>
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiWaterSub', 'هدف: {{target}} ml', { target: fmt(userSettings.targetWater || 2000) })}</span>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-1">
          <div className="flex items-center justify-between text-gray-400">
            <span className="text-[10px] font-bold uppercase tracking-wider">{t('reportsKpiSleep', 'میانگین خواب')}</span>
            <Moon className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400 font-mono">
            {fmt(totals.avgSleep)} <span className="text-xs text-gray-400 font-normal">{t('reportsKpiSleepUnit', 'ساعت')}</span>
          </div>
          <span className="text-[10px] text-gray-400 block">{t('reportsKpiSleepSub', 'هدف: ۷ تا ۸ ساعت روزانه')}</span>
        </div>

      </div>

      {/* SECTION 1: VISUAL CHARTS DASHBOARD */}
      {activeSection === 'charts' && (
        <div className="space-y-6 animate-fadeIn">
          
          {/* Chart 1: Spine Rehab Exercises vs Pain Severity Level Correlation */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-3 border-gray-100 dark:border-gray-700/60">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  {t('reportsChart1Title', 'رابطه تمرینات فیزیوتراپی و کاهش درد کمر (Spine Rehab vs Pain Correlation)')}
                </h3>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('reportsChart1Sub', 'مقایسه تعداد تمرینات فیزیوتراپی انجام‌شده (ستون‌های سبز) با شدت درد کمر ثبت‌شده (خط نارنجی).')}
                </p>
              </div>
              <div className="flex items-center gap-4 text-[11px]">
                <span className="flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400">
                  <span className="w-3 h-3 rounded-xs bg-emerald-500 inline-block"></span> {t('reportsChart1LegendBar', 'تعداد تمرین فیزیوتراپی')}
                </span>
                <span className="flex items-center gap-1.5 font-semibold text-orange-600 dark:text-orange-400">
                  <span className="w-3 h-0.5 bg-orange-500 inline-block"></span> {t('reportsChart1LegendLine', 'میزان درد (0-10)')}
                </span>
              </div>
            </div>

            <div className="h-72 w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={recordsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.15} />
                  <XAxis dataKey="shortDate" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                  <YAxis yAxisId="left" orientation="left" domain={[0, 8]} tick={{ fontSize: 10, fill: '#10B981' }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 10]} tick={{ fontSize: 10, fill: '#F97316' }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', fontSize: '12px' }}
                    formatter={(value: any, name: string) => [
                      name === 'rehabCount' ? `${fmt(value)} ${t('reportsUnitDrills', 'حرکت فیزیوتراپی')}` : `${fmt(value)} ${t('reportsUnitOf10', 'از ۱۰')}`,
                      name === 'rehabCount' ? t('reportsChart1TtBar', 'تمرینات کمر') : t('reportsChart1TtLine', 'شدت درد ستون فقرات')
                    ]}
                    labelFormatter={(label) => `${t('reportsChartDatePrefix', 'تاریخ:')} ${label}`}
                  />
                  <Bar yAxisId="left" dataKey="rehabCount" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={30} name="rehabCount" />
                  <Line yAxisId="right" type="monotone" dataKey="painLevel" stroke="#F97316" strokeWidth={3} dot={{ r: 4, fill: '#F97316' }} activeDot={{ r: 6 }} name="painLevel" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart Grid: Calories & Protein + Hydration & Sleep */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Chart 2: Daily Calories & Protein */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="flex justify-between items-center border-b pb-3 border-gray-100 dark:border-gray-700/60">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Apple className="w-4 h-4 text-amber-500" />
                  {t('reportsChart2Title', 'کالری دریافتی و پروتئین روزانه')}
                </h3>
                <span className="text-[11px] text-gray-400 font-mono">{t('reportsChart2Target', 'سقف target: {{target}} kcal', { target: fmt(userSettings.targetCalories || 1767) })}</span>
              </div>

              <div className="h-64 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={recordsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.15} />
                    <XAxis dataKey="shortDate" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#F59E0B' }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#10B981' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', fontSize: '12px' }}
                      formatter={(val: any, name: string) => [
                        name === 'foodCalories' ? `${fmt(val)} kcal` : `${fmt(val)} g`,
                        name === 'foodCalories' ? t('reportsChart2TtCal', 'کالری مصرفی') : t('reportsChart2TtProt', 'پروتئین دریافتی')
                      ]}
                    />
                    <ReferenceLine yAxisId="left" y={userSettings.targetCalories || 1767} stroke="#EF4444" strokeDasharray="3 3" label={{ value: t('reportsTargetLabel', 'هدف'), fill: '#EF4444', fontSize: 10 }} />
                    <Bar yAxisId="left" dataKey="foodCalories" fill="#F59E0B" radius={[6, 6, 0, 0]} maxBarSize={25} name="foodCalories" />
                    <Line yAxisId="right" type="monotone" dataKey="foodProtein" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} name="foodProtein" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 3: Hydration & Sleep */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="flex justify-between items-center border-b pb-3 border-gray-100 dark:border-gray-700/60">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  {t('reportsChart3Title', 'هیدراتاسیون آب (ml) و ساعات خواب')}
                </h3>
                <span className="text-[11px] text-gray-400 font-mono">{t('reportsChart3Target', 'هدف آب: {{target}} ml', { target: fmt(userSettings.targetWater || 2000) })}</span>
              </div>

              <div className="h-64 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={recordsData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.15} />
                    <XAxis dataKey="shortDate" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#3B82F6' }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 12]} tick={{ fontSize: 10, fill: '#6366F1' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', fontSize: '12px' }}
                      formatter={(val: any, name: string) => [
                        name === 'water' ? `${fmt(val)} ml` : `${fmt(val)} ${t('reportsHoursUnit', 'ساعت')}`,
                        name === 'water' ? t('reportsChart3TtWater', 'مصرف آب') : t('reportsChart3TtSleep', 'ساعات خواب')
                      ]}
                    />
                    <ReferenceLine yAxisId="left" y={userSettings.targetWater || 2000} stroke="#3B82F6" strokeDasharray="3 3" />
                    <Area yAxisId="left" type="monotone" dataKey="water" fill="#3B82F6" stroke="#2563EB" fillOpacity={0.2} name="water" />
                    <Line yAxisId="right" type="monotone" dataKey="sleep" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 3 }} name="sleep" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          {/* Chart Grid: Weight Trend + Macronutrient Pie */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Chart 4: Weight Trajectory (2 Cols) */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="flex justify-between items-center border-b pb-3 border-gray-100 dark:border-gray-700/60">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  {t('reportsChart4Title', 'روند تغییرات وزن و هدف (Weight Trajectory)')}
                </h3>
                <span className="text-[11px] text-gray-400 font-mono">
                  {t('reportsChart4Goal', 'هدف: {{goal}}', { goal: userSettings.weightGoal ? fmt(userSettings.weightGoal) + ' kg' : t('reportsUndefined', 'تعریف نشده') })}
                </span>
              </div>

              <div className="h-60 w-full pt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={recordsData.filter(d => d.weight && d.weight > 0)} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.15} />
                    <XAxis dataKey="shortDate" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10, fill: '#10B981' }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', fontSize: '12px' }}
                      formatter={(val: any) => [`${fmt(val)} kg`, t('reportsChart4TtWeight', 'وزن ثبت‌شده')]}
                    />
                    {userSettings.weightGoal && (
                      <ReferenceLine y={userSettings.weightGoal} stroke="#10B981" strokeDasharray="3 3" label={{ value: t('reportsChart4GoalLabel', 'وزن هدف'), fill: '#10B981', fontSize: 10 }} />
                    )}
                    <Line type="monotone" dataKey="weight" stroke="#10B981" strokeWidth={3} dot={{ r: 5, fill: '#10B981' }} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Chart 5: Macronutrient Pie Chart (1 Col) */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4 flex flex-col justify-between">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700/60">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <PieIcon className="w-4 h-4 text-purple-500" />
                  {t('reportsChart5Title', 'نسبت درشتمغذی‌ها (Macros)')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsChart5Sub', 'میانگین پروتئین، کربوهیدرات و چربی روزانه')}</p>
              </div>

              <div className="h-44 w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={macroPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {macroPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F2937', borderColor: '#374151', borderRadius: '12px', color: '#fff', fontSize: '11px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-gray-100 dark:border-gray-700/60 text-xs">
                {macroPieData.map((m, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: m.color }}></span>
                      {m.name}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white font-mono">{fmt(m.value)}g</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>
      )}

      {/* SECTION 2: FULL NUTRITION & FOODS REPORT */}
      {activeSection === 'nutrition' && (
        <div className="space-y-6 animate-fadeIn">
          
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4 border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Apple className="w-5 h-5 text-emerald-500" />
                  {t('reportsNutritionTitle', 'لیست جامع مواد غذایی و درشتمغذی‌های ثبت‌شده')}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {t('reportsNutritionCount', 'تعداد کل غذاهای ثبت‌شده در این بازه: {{count}} مورد', { count: fmt(totals.allFoodsList.length) })}
                </p>
              </div>

              {/* Food search box */}
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={t('reportsFoodSearchPlaceholder', 'جستجوی غذا یا وعده...')}
                  value={foodSearch}
                  onChange={(e) => setFoodSearch(e.target.value)}
                  className="w-full pr-9 pl-3 py-2 text-xs border border-gray-200 dark:border-gray-700 rounded-xl bg-slate-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Food items table */}
            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 uppercase text-[10px]">
                    <th className="py-2.5 px-3">{t('reportsThDate', 'تاریخ')}</th>
                    <th className="py-2.5 px-3">{t('reportsThFoodName', 'نام ماده غذایی')}</th>
                    <th className="py-2.5 px-3">{t('reportsThMeal', 'وعده')}</th>
                    <th className="py-2.5 px-3">{t('reportsThQty', 'مقدار')}</th>
                    <th className="py-2.5 px-3">{t('reportsThCalories', 'کالری (kcal)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThProtein', 'پروتئین (g)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThCarbs', 'کربوهیدرات (g)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThFat', 'چربی (g)')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {filteredFoods.length > 0 ? (
                    filteredFoods.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-mono text-gray-400 text-[11px] whitespace-nowrap">{item.date}</td>
                        <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">{getLocalizedFoodText(item.food.name, lang)}</td>
                        <td className="py-2.5 px-3">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
                            {item.food.category === 'Breakfast' ? t('reportsMealBreakfast', 'صبحانه') : item.food.category === 'Lunch' ? t('reportsMealLunch', 'ناهار') : item.food.category === 'Dinner' ? t('reportsMealDinner', 'شام') : t('reportsMealSnack', 'میان‌وعده')}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300">{getLocalizedFoodQuantity(item.food.quantity, lang)}</td>
                        <td className="py-2.5 px-3 font-bold text-amber-600 dark:text-amber-400 font-mono">{fmt(item.food.calories)}</td>
                        <td className="py-2.5 px-3 font-bold text-emerald-600 dark:text-emerald-400 font-mono">{fmt(item.food.protein)}</td>
                        <td className="py-2.5 px-3 text-blue-600 dark:text-blue-400 font-mono">{fmt(item.food.carbs)}</td>
                        <td className="py-2.5 px-3 text-yellow-600 dark:text-yellow-400 font-mono">{fmt(item.food.fat)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-400">
                        {t('reportsFoodNotFound', 'هیچ ماده غذایی در این بازه یا با این عبارت جستجو یافت نشد.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>

        </div>
      )}

      {/* SECTION 3: REHAB & EXERCISES REPORT */}
      {activeSection === 'rehab' && (
        <div className="space-y-6 animate-fadeIn">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Rehab Drills List & Completion Count */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-500" />
                  {t('reportsRehabDrillsTitle', 'تکمیل تمرینات فیزیوتراپی و التیام دیسک کمر')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsRehabDrillsSub', 'میزان تکرار هر حرکت فیزیوتراپی در بازه انتخابی')}</p>
              </div>

              <div className="space-y-3">
                {rehabExercisesList.map((ex) => {
                  const count = recordsData.reduce((sum, d) => sum + (d.record.rehabCompletedIds?.includes(ex.id) ? 1 : 0), 0);
                  const percentage = Math.round((count / (recordsData.length || 1)) * 100);

                  return (
                    <div key={ex.id} className="p-3 bg-slate-50 dark:bg-gray-900/50 rounded-xl space-y-1.5 border border-gray-100 dark:border-gray-800">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-gray-900 dark:text-white">{ex.name}</span>
                        <span className="text-purple-600 dark:text-purple-400 font-bold font-mono">
                          {t('reportsTimesCount', '{{count}} بار ({{percent}}٪)', { count: fmt(count), percent: fmt(percentage) })}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-600 rounded-full transition-all" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Belly Fat & Cardio Exercises List */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Flame className="w-4 h-4 text-amber-500" />
                  {t('reportsFatLossTitle', 'تمرینات چربی‌سوزی کاردیو و آب‌کردن شکم')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsFatLossSub', 'میزان اجرای تمرینات هوازی و کالری‌سوز')}</p>
              </div>

              <div className="space-y-3">
                {fatLossExercisesList.map((ex) => {
                  const count = recordsData.reduce((sum, d) => sum + (d.record.fatLossCompletedIds?.includes(ex.id) ? 1 : 0), 0);
                  const percentage = Math.round((count / (recordsData.length || 1)) * 100);

                  return (
                    <div key={ex.id} className="p-3 bg-slate-50 dark:bg-gray-900/50 rounded-xl space-y-1.5 border border-gray-100 dark:border-gray-800">
                      <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-gray-900 dark:text-white">{ex.name}</span>
                        <span className="text-amber-600 dark:text-amber-400 font-bold font-mono">
                          {t('reportsTimesCount', '{{count}} بار ({{percent}}٪)', { count: fmt(count), percent: fmt(percentage) })}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

        </div>
      )}

      {/* SECTION 4: SLEEP, WORK & SWEDISH STUDY LOGS */}
      {activeSection === 'sleep_work' && (
        <div className="space-y-6 animate-fadeIn">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Sleep Summary */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  {t('reportsSleepSummaryTitle', 'خلاصه خواب و ریکاوری شبانه')}
                </h3>
              </div>
              <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsAvgSleepLabel', 'میانگین خواب:')}</span>
                  <span className="font-bold text-indigo-600 dark:text-indigo-400 font-mono">{fmt(totals.avgSleep)} {t('reportsHoursUnit', 'ساعت')}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsSleepGoalLabel', 'هدف استاندارد خواب:')}</span>
                  <span className="font-bold font-mono">{t('reportsSleepGoalVal', '۷ تا ۸ ساعت')}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>{t('reportsSleepDiscRole', 'نقش در ترمیم دیسک کمر:')}</span>
                  <span className="font-bold text-emerald-600">{t('reportsSleepDiscRoleVal', 'بسیار بالا (عامل کلیدی)')}</span>
                </div>
              </div>
            </div>

            {/* Work & Activities */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  {t('reportsWorkSummaryTitle', 'شیفت‌های کاری و فعالیت‌ها')}
                </h3>
              </div>
              <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsWorkDaysCount', 'تعداد شیفت کاری ثبت‌شده:')}</span>
                  <span className="font-bold text-blue-600 dark:text-blue-400 font-mono">{fmt(totals.totalWorkDays)} {t('reportsShiftsUnit', 'شیفت')}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsActiveMinutes', 'کل دقایق فعالیت بدنی:')}</span>
                  <span className="font-bold font-mono">{fmt(totals.totalActiveMinutes)} {t('reportsMinUnit', 'دقیقه')}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>{t('reportsCaloriesBurned', 'کالری سوزانده‌شده ورزشی:')}</span>
                  <span className="font-bold text-rose-500 font-mono">{fmt(totals.totalCaloriesBurned)} kcal</span>
                </div>
              </div>
            </div>

            {/* Energy Level & Spine Pain Index */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
              <div className="border-b pb-3 border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Heart className="w-4 h-4 text-rose-500" />
                  {t('reportsEnergyPainSummaryTitle', 'سطح انرژی و کنترل درد')}
                </h3>
              </div>
              <div className="space-y-2 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsAvgEnergyLabel', 'میانگین سطح انرژی بدنی:')}</span>
                  <span className="font-bold text-amber-500 font-mono">{fmt(totals.avgEnergy)} {t('reportsOf5Unit', 'از ۵')}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                  <span>{t('reportsAvgPainLabel', 'میانگین شدت درد کمر:')}</span>
                  <span className="font-bold text-rose-600 dark:text-rose-400 font-mono">{fmt(totals.avgPain)} {t('reportsOf10Unit', 'از ۱۰')}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span>{t('reportsAvgWaterLabel', 'وضعیت هیدراتاسیون آب:')}</span>
                  <span className="font-bold text-blue-600 dark:text-blue-400 font-mono">{fmt(totals.avgWater)} ml</span>
                </div>
              </div>
            </div>

          </div>

          {/* TABLE 1: DAILY SLEEP & NIGHTLY RECOVERY LOG FOR EVERY DAY */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Moon className="w-4 h-4 text-indigo-500" />
                  {t('reportsTable1Title', 'جدول روزانه پایش خواب و ریکاوری شبانه (Daily Sleep & Recovery Log)')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsTable1Sub', 'گزارش تک‌تک روزهای ثبت‌شده با میزان ساعات استراحت، سطح انرژی و وضعیت درد کمر')}</p>
              </div>
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 font-mono">
                {t('reportsTotalDaysCount', 'مجموع روزها: {{count}} روز', { count: fmt(recordsData.length) })}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 uppercase text-[10px]">
                    <th className="py-2.5 px-3">{t('reportsThDate', 'تاریخ')}</th>
                    <th className="py-2.5 px-3">{t('reportsThSleepHours', 'میزان خواب (ساعت)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThRestEval', 'ارزیابی استراحت')}</th>
                    <th className="py-2.5 px-3">{t('reportsThEnergy15', 'سطح انرژی (۱-۵)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThPain010', 'درد کمر (۰-۱۰)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThMood', 'حس و حال (Mood)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThShift', 'شیفت کاری')}</th>
                    <th className="py-2.5 px-3">{t('reportsThNotes', 'یادداشت و علائم')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[...recordsData].reverse().map((d, idx) => {
                    const sleepVal = d.sleep;
                    let sleepBadgeClass = 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-gray-300';
                    let sleepStatusText = t('reportsSleepNotRecorded', 'ثبت‌نشده');

                    if (sleepVal >= 7.5) {
                      sleepBadgeClass = 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50';
                      sleepStatusText = t('reportsSleepIdeal', 'عالی و ایده‌آل');
                    } else if (sleepVal >= 6.5) {
                      sleepBadgeClass = 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200/50';
                      sleepStatusText = t('reportsSleepGood', 'خوب و مناسب');
                    } else if (sleepVal > 0) {
                      sleepBadgeClass = 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200/50';
                      sleepStatusText = t('reportsSleepLow', 'کمتر از استاندارد');
                    }

                    return (
                      <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-mono text-gray-900 dark:text-white font-bold whitespace-nowrap">{d.date}</td>
                        <td className="py-2.5 px-3 font-bold text-indigo-600 dark:text-indigo-400 font-mono">
                          {sleepVal > 0 ? `${fmt(sleepVal)} ${t('reportsHoursUnit', 'ساعت')}` : '-'}
                        </td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${sleepBadgeClass}`}>
                            {sleepStatusText}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-amber-500">
                          {d.energyLevel > 0 ? '⚡'.repeat(d.energyLevel) : '-'}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-rose-500">
                          {d.painLevel > 0 ? `${fmt(d.painLevel)} / ${fmt(10)}` : t('reportsNoPain', '۰ (بدون درد)')}
                        </td>
                        <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 font-medium">
                          {d.record.notes?.mood || t('reportsMoodCalm', 'آرام')}
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          {d.wentToWork ? (
                            <span className="px-1.5 py-0.5 rounded-md text-[9px] bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-bold">{t('reportsShiftWorkTag', 'شیفت کاری')}</span>
                          ) : (
                            <span className="text-gray-400 text-[10px]">-</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-gray-500 dark:text-gray-400 text-[11px] max-w-xs truncate">
                          {d.record.notes?.observations || d.record.notes?.symptoms || d.record.notes?.reminders || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* TABLE 2: DAILY WATER CONSUMPTION LOG FOR EVERY DAY */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  {t('reportsTable2Title', 'جدول روزانه آب مصرفی (Daily Water Consumption Log)')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsTable2Sub', 'ثبت دقیق هیدراتاسیون روزانه به میلی‌لیتر و درصد دستیابی به هدف')}</p>
              </div>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 font-mono">
                {t('reportsDailyWaterGoal', 'هدف روزانه: {{target}} ml', { target: fmt(userSettings.targetWater || 2000) })}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-right text-xs">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 uppercase text-[10px]">
                    <th className="py-2.5 px-3">{t('reportsThDate', 'تاریخ')}</th>
                    <th className="py-2.5 px-3">{t('reportsThWaterMl', 'میزان آب (ml)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThGoalMl', 'هدف (ml)')}</th>
                    <th className="py-2.5 px-3">{t('reportsThProgressPercent', 'درصد پیشرفت')}</th>
                    <th className="py-2.5 px-3">{t('reportsThHydrationStatus', 'وضعیت هیدراتاسیون')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {[...recordsData].reverse().map((d, idx) => {
                    const targetWater = userSettings.targetWater || 2000;
                    const waterVal = d.water;
                    const percent = Math.min(Math.round((waterVal / targetWater) * 100), 100);

                    let waterStatusBadge = 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200/50';
                    let waterStatusText = t('reportsWaterLow', 'کمتر از حد مجاز');

                    if (percent >= 100) {
                      waterStatusBadge = 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200/50';
                      waterStatusText = t('reportsWaterFull', '💧 هیدراته کامل (عالی)');
                    } else if (percent >= 70) {
                      waterStatusBadge = 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-400 border border-cyan-200/50';
                      waterStatusText = t('reportsWaterGood', 'مناسب و مطلوب');
                    } else if (waterVal > 0) {
                      waterStatusBadge = 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200/50';
                      waterStatusText = t('reportsWaterNeedMore', 'نیازمند افزایش نوشیدن');
                    }

                    return (
                      <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="py-2.5 px-3 font-mono text-gray-900 dark:text-white font-bold whitespace-nowrap">{d.date}</td>
                        <td className="py-2.5 px-3 font-bold text-blue-600 dark:text-blue-400 font-mono">
                          {fmt(waterVal)} ml
                        </td>
                        <td className="py-2.5 px-3 text-gray-400 font-mono">
                          {fmt(targetWater)} ml
                        </td>
                        <td className="py-2.5 px-3 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${percent}%` }}></div>
                            </div>
                            <span className="font-bold text-[11px] font-mono text-gray-700 dark:text-gray-300 w-10 text-left">
                              {fmt(percent)}٪
                            </span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold ${waterStatusBadge}`}>
                            {waterStatusText}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* SECTION 5: AI EXPORT & DATA BACKUP CENTER */}
      {activeSection === 'ai_export' && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 animate-fadeIn">
          
          {/* AI Markdown Code Output (8 cols) */}
          <div className="md:col-span-8 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-4">
            <div className="flex justify-between items-center border-b pb-3 border-gray-100 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  {t('reportsAiExportTitle', 'کد خروجی ساختاریافته برای چت هوش مصنوعی (ChatGPT / Gemini / Claude)')}
                </h3>
                <p className="text-[11px] text-gray-400 mt-0.5">{t('reportsAiExportSub', 'با قرار دادن این متن در چت، مربی هوشمند تحلیلی ارگونومیک ارائه خواهد کرد.')}</p>
              </div>

              <button
                onClick={handleCopyMarkdown}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? t('reportsCopied', 'کپی شد!') : t('reportsCopyBtn', 'کپی کل متن خروجی')}</span>
              </button>
            </div>

            <div className="bg-slate-950 p-4 rounded-xl border border-gray-800 max-h-96 overflow-y-auto text-xs font-mono text-gray-300 text-left leading-relaxed" dir="ltr">
              <pre className="whitespace-pre-wrap font-mono">{aiMarkdownText}</pre>
            </div>
          </div>

          {/* Download Data Files (4 cols) */}
          <div className="md:col-span-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-800/80 shadow-xs space-y-5 flex flex-col justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Download className="w-4 h-4 text-emerald-500" />
                {t('reportsDownloadTitle', 'دانلود و پشتیبان‌گیری از تمام داده‌ها')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                {t('reportsDownloadDesc', 'می‌توانید گزارش کامل داده‌های خود را به صورت CSV برای استفاده در نرم‌افزارهایی مثل اکسل یا گزارش‌های پزشکی و فایل JSON کامل دانلود کنید.')}
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <button
                onClick={handleExportCSV}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer"
              >
                <FileText className="w-4 h-4" />
                <span>{t('reportsDownloadCsvBtn', 'دانلود خروجی CSV (مناسب پزشک و اکسل)')}</span>
              </button>

              <button
                onClick={handleExportJSON}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border border-gray-200 dark:border-gray-600"
              >
                <Download className="w-4 h-4" />
                <span>{t('reportsDownloadJsonBtn', 'دانلود پشتیبان کامل JSON')}</span>
              </button>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
